import { CardContent, Divider, Stack, Typography, useTheme } from "@mui/material";
import { DataDownloadEntry, DataLinkEntry } from "src/components/cards/custom/data-links-card/SeneInfoLinkEntries";
import { EntriesSkeletons } from "src/components/cards/custom/common/EntriesSkeletons";
import { SenecBaseTheme } from "src/styles/senecBaseTheme";
import { isNotLastItem } from "src/components/cards/custom/common/utils";
import { SenecCard } from "src/components/cards/base/SenecCard";

export interface InfoLink {
    id: string;
    title: string;
    subtitle: string;
    document?: string;
    website?: string;
}

export type CardMessages = {
    title: string;
    website: string;
    download: string;
    noContent: string;
};

export interface InfoLinksCardProps {
    infoLinks?: InfoLink[];
    linkAsIcon?: boolean;
    messages: CardMessages;
    isLoading: boolean;
    error?: Error;
    "data-testid"?: string;
}

export const SenecInfoLinksCard = ({
    infoLinks,
    messages,
    isLoading,
    error,
    linkAsIcon = false,
    "data-testid": dataTestId = "data-links-card",
}: InfoLinksCardProps) => {
    const theme: SenecBaseTheme = useTheme();

    const showSkeletonOrEntries = () => {
        if (isLoading) {
            return <EntriesSkeletons total={3} />;
        } else if (!!error || !infoLinks || infoLinks.length < 1) {
            return <Typography>{messages.noContent}</Typography>;
        } else {
            return (
                <>
                    {infoLinks.map((data, idx) => (
                        <Stack key={data.id} spacing={2}>
                            {/* Entry */}
                            {linkAsIcon ? <DataDownloadEntry data={data} /> : <DataLinkEntry data={data} messages={messages} />}
                            {/* Divider */}
                            {isNotLastItem(infoLinks.length, idx) && (
                                <Divider sx={{ borderColor: theme.palette.neutralGray30.main }} />
                            )}
                        </Stack>
                    ))}
                </>
            );
        }
    };

    return (
        <SenecCard
            data-testid={dataTestId}
            sx={{
                padding: 0,
                height: "100%",
            }}
        >
            <CardContent sx={{ padding: 3 }}>
                <Stack spacing={3}>
                    <Typography variant="h4">{messages.title}</Typography>
                    <Stack spacing={2}>{showSkeletonOrEntries()}</Stack>
                </Stack>
            </CardContent>
        </SenecCard>
    );
};
